<script>
  import { tick } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { expoOut } from 'svelte/easing';
  import { commandState, closeSiri } from '../os/commands.svelte.js';
  import { APPS } from '../os/apps.js';
  import { launchApp as launchRegisteredApp } from '../os/windows.svelte.js';
  import { allFiles, emptyTrash } from '../os/filesystem.svelte.js';
  import { openFile } from '../os/documents.svelte.js';
  import { portfolio } from '../os/portfolio.svelte.js';
  import Icon from './Icon.svelte';

  let inputEl = $state();
  const siriFx = $derived(portfolio.settings.reduceMotion ? { duration: 0 } : { y: -30, duration: 500, easing: expoOut });
  const siriBg = $derived(portfolio.settings.reduceMotion ? { duration: 0 } : { duration: 200 });

  const commands = [
    { id: 'empty-trash', label: 'Empty Trash', detail: 'System command', icon: 'trash', run: emptyTrash },
    { id: 'new-finder', label: 'New Finder Window', detail: 'System command', icon: 'finder', run: () => launchRegisteredApp(APPS.find((app) => app.id === 'projects')) },
    { id: 'open-photos', label: 'Open Photos Library', detail: 'Creative command', icon: 'image-file', run: () => launchRegisteredApp(APPS.find((app) => app.id === 'photos')) },
  ];

  let results = $derived(() => {
    const q = commandState.query.toLowerCase().trim();
    if (!q) return [];
    const apps = APPS
      .filter((app) => app.label.toLowerCase().includes(q) || app.category.toLowerCase().includes(q))
      .map((app) => ({ type: 'app', id: app.id, label: app.label, detail: 'Application', icon: app.icon, value: app }));
    const files = allFiles()
      .filter((file) => file.name.toLowerCase().includes(q))
      .slice(0, 6)
      .map((file) => ({ type: 'file', id: file.path, label: file.name, detail: file.path, icon: file.icon, value: file }));
    const matchingCommands = commands
      .filter((command) => command.label.toLowerCase().includes(q))
      .map((command) => ({ type: 'command', ...command }));
    return [...matchingCommands, ...apps, ...files].slice(0, 10);
  });

  // Automatically focus the input when Siri opens
  $effect(() => {
    if (commandState.isSiriOpen && inputEl) {
      tick().then(() => inputEl.focus());
    }
  });

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      closeSiri();
      e.preventDefault();
    } else if (e.key === 'Enter') {
      const res = results();
      if (res.length > 0) {
        launchResult(res[0]);
      }
    }
  }

  function launchApp(app) {
    void launchRegisteredApp(app);
    closeSiri();
  }

  function launchResult(result) {
    if (result.type === 'app') launchApp(result.value);
    if (result.type === 'file') {
      void openFile(result.value);
      closeSiri();
    }
    if (result.type === 'command') {
      result.run();
      closeSiri();
    }
  }
</script>

{#if commandState.isSiriOpen}
  <div class="siri-backdrop" transition:fade={siriBg} onclick={closeSiri} role="presentation" aria-hidden="true"></div>
  <div class="siri-container" transition:fly={siriFx}>
    <div class="siri-search-bar">
      <span class="siri-icon">🔍</span>
      <input 
        bind:this={inputEl}
        bind:value={commandState.query}
        onkeydown={handleKeydown}
        placeholder="Search apps, files, and commands…"
        autocomplete="off"
        spellcheck="false"
      />
    </div>

    {#if commandState.query.trim().length > 0}
      <div class="siri-results">
        {#each results() as result}
          <div class="siri-result-item" onclick={() => launchResult(result)} role="button" tabindex="0" onkeydown={(e) => { if (e.key === 'Enter') launchResult(result); }}>
            <div class="result-icon">
              <Icon name={result.icon} size={32} />
            </div>
            <div class="result-info">
              <div class="result-title">{result.label}</div>
              <div class="result-type">{result.detail}</div>
            </div>
          </div>
        {:else}
          <div class="siri-no-results">No results found</div>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style>
  .siri-backdrop {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 99999;
    background: rgba(0,0,0,0.2);
    /* No backdrop-filter here, we want the spotlight to be sharp */
  }

  .siri-container {
    position: fixed;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    max-width: 90vw;
    z-index: 100000;
    display: flex;
    flex-direction: column;
    filter: drop-shadow(0 20px 40px rgba(0,0,0,0.5));
  }

  .siri-search-bar {
    display: flex;
    align-items: center;
    background: rgba(30, 30, 30, 0.85);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    padding: 12px 20px;
  }

  .siri-icon {
    font-size: 24px;
    margin-right: 16px;
    opacity: 0.7;
  }

  .siri-search-bar input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 28px;
    font-weight: 300;
    font-family: var(--font);
  }

  .siri-search-bar input::placeholder {
    color: rgba(255,255,255,0.3);
  }

  .siri-results {
    margin-top: 8px;
    background: rgba(30, 30, 30, 0.85);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    overflow: hidden;
  }

  .siri-result-item {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    cursor: pointer;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }

  .siri-result-item:last-child {
    border-bottom: none;
  }

  .siri-result-item:hover {
    background: rgba(255,255,255,0.1);
  }

  .result-icon {
    margin-right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .result-info {
    display: flex;
    flex-direction: column;
  }

  .result-title {
    color: #fff;
    font-size: 16px;
    font-family: var(--font);
  }

  .result-type {
    color: #aaa;
    font-size: 12px;
    font-family: var(--font);
  }

  .siri-no-results {
    padding: 20px;
    text-align: center;
    color: #888;
    font-family: var(--font);
  }
</style>
